/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\ServerSshKeyBox.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the telephonyServers module.
----------------------------------------------------------------*/


import { Key, Copy, Check, Shield } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { useI18n } from '../../../hooks/useI18n';

type SshKeyStatus = 'none' | 'generating' | 'generated' | 'copying' | 'copied' | 'error';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FormData = Record<string, any>;

interface Props {
    formData: FormData;
    isGeneratingKey: boolean;
    sshKeyStatus: SshKeyStatus;
    generatedSshKey: string | null;
    isCopied: boolean;
    sshStatusMessage: string;
    sshButtonLabel: string;
    onGenerateSshKey: (setErrors: (e: Record<string, string>) => void) => void;
    onCopySshKey: () => void;
    setErrors: (e: Record<string, string>) => void;
}

export function ServerSshKeyBox({
    formData, isGeneratingKey, sshKeyStatus, generatedSshKey, isCopied,
    sshStatusMessage, sshButtonLabel, onGenerateSshKey, onCopySshKey, setErrors,
}: Props) {
    const { t } = useI18n();

    const buttonCls = isGeneratingKey
        ? 'text-muted-foreground bg-muted border-border cursor-not-allowed'
        : sshKeyStatus === 'generated' || sshKeyStatus === 'copied' || sshKeyStatus === 'copying'
            ? 'text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 bg-green-100/50 dark:bg-green-900/30 border-green-200 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-900/40'
            : sshKeyStatus === 'error'
                ? 'text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 bg-red-100/50 dark:bg-red-900/30 border-red-200 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-900/40'
                : 'text-primary hover:text-primary/80 hover:bg-primary/10 border-primary/20';

    const showKey = (sshKeyStatus === 'generated' || sshKeyStatus === 'copying' || sshKeyStatus === 'copied') && generatedSshKey;
    const isCopiedState = sshKeyStatus === 'copied';
    const connectCmd = `ssh ${formData.username || 'root'}@${formData.host || 'host'} -p ${formData.port || 22}`;

    return (
        <FormBox title={t('telephonyServers.sections.sshKey')} icon={<Shield className="h-5 w-5" />}>
            <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-muted-foreground">{sshStatusMessage}</p>
                <SimpleTooltip content={t('telephonyServers.tooltips.generateInstallSshKey')} position="top" maxWidth="300px">
                    <button type="button" onClick={() => onGenerateSshKey(setErrors)} disabled={isGeneratingKey} className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors border ${buttonCls}`}>
                        <Key className={`h-4 w-4 ${isGeneratingKey ? 'animate-spin' : ''}`} />
                        {sshButtonLabel}
                    </button>
                </SimpleTooltip>
            </div>

            {showKey && (
                <div className="space-y-3">
                    <div>
                        <label className="text-sm font-medium text-foreground">{t('telephonyServers.sshKey.generatedKeyLabel')}</label>
                        <div className="mt-2 rounded-lg border border-border bg-muted p-3">
                            <div className="flex items-start justify-between gap-2">
                                <code className="flex-1 break-all font-mono text-xs text-foreground">{generatedSshKey}</code>
                                <button type="button" onClick={onCopySshKey} className="flex items-center gap-1 rounded px-2 py-1 text-xs text-primary transition-colors hover:bg-primary/10 hover:text-primary/80">
                                    {isCopied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                                    {isCopied ? t('telephonyServers.sshKey.actions.copied') : t('telephonyServers.sshKey.actions.copy')}
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className={`rounded-lg border p-3 text-xs ${isCopiedState ? 'border-green-200 bg-green-50 text-green-800' : 'border-blue-200 bg-blue-50 text-blue-800'}`}>
                        <h5 className={`mb-2 text-sm font-medium ${isCopiedState ? 'text-green-900' : 'text-blue-900'}`}>
                            {t(isCopiedState ? 'telephonyServers.sshKey.instructions.installedTitle' : 'telephonyServers.sshKey.instructions.manualTitle')}
                        </h5>
                        {isCopiedState ? (
                            <div className="space-y-1.5">
                                <p>{t('telephonyServers.sshKey.instructions.installedLine1')}</p>
                                <p>{t('telephonyServers.sshKey.instructions.installedTest', { command: connectCmd })}</p>
                                <p>{t('telephonyServers.sshKey.instructions.installedNext')}</p>
                            </div>
                        ) : (
                            <div className="space-y-1.5">
                                <p>{t('telephonyServers.sshKey.instructions.manualStep1', { command: connectCmd })}</p>
                                <div className="space-y-1 rounded bg-blue-100 p-2 font-mono text-xs text-blue-900">
                                    <code>{t('telephonyServers.sshKey.instructions.manualCommand1')}</code>
                                    <code>{`echo "${generatedSshKey}" >> ~/.ssh/authorized_keys`}</code>
                                    <code>{t('telephonyServers.sshKey.instructions.manualCommand3')}</code>
                                </div>
                                <p className="font-medium text-blue-700">{t('telephonyServers.sshKey.instructions.manualHint')}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </FormBox>
    );
}
